import { lazy, Suspense, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../Buttons/Button";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  BULK_CAMPAIGNS_CONTENTS_COUNTER,
  BULK_CAMPAIGNS_CONTENTS_HINT,
  BULK_CAMPAIGNS_REMOVE_CONTENT,
  BULK_CAMPAIGNS_SELECT_CONTENTS,
} from "../../../i18n/keysTranslations";
import { BULK_MAX_CONTENTS } from "../../../utils/bulkCampaignContent";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";

const ModalGallery = lazy(() => import("../ModalGallery"));

const thumbSx = {
  width: 72,
  height: 72,
  objectFit: "cover",
  borderRadius: 1,
  backgroundColor: "action.hover",
};

const ContentThumb = ({ content }) => {
  if (content?.type === "video" && content?.url) {
    return <Box component="video" src={content.url} muted sx={thumbSx} />;
  }

  if (content?.url) {
    return <Box component="img" alt="" src={content.url} sx={thumbSx} />;
  }

  return <Box sx={thumbSx} />;
};

const BulkCampaignsContents = ({
  contents = [],
  onContentsSelected,
  onGallerySelected,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const [galleryOpen, setGalleryOpen] = useState(false);
  const count = contents.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Button color="secondary" onClick={() => setGalleryOpen(true)}>
          {t(BULK_CAMPAIGNS_SELECT_CONTENTS)}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {t(BULK_CAMPAIGNS_CONTENTS_COUNTER, {
            count,
            limit: BULK_MAX_CONTENTS,
          })}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {t(BULK_CAMPAIGNS_CONTENTS_HINT)}
      </Typography>
      {count > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {contents.map((content) => (
            <Box key={content.contentID} sx={{ position: "relative" }}>
              <ContentThumb content={content} />
              <IconButton
                size="small"
                aria-label={t(BULK_CAMPAIGNS_REMOVE_CONTENT)}
                onClick={() =>
                  onContentsSelected(
                    contents.filter(
                      (item) => item.contentID !== content.contentID
                    )
                  )
                }
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  backgroundColor: "background.paper",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {galleryOpen && (
        <Suspense fallback={null}>
          <ModalGallery
            modalOpen={galleryOpen}
            multiple={true}
            maxSelections={BULK_MAX_CONTENTS}
            showFooterUseSelectedButton={true}
            showAdAccountOption={false}
            showSocialPostsOption={false}
            showGoogleDriveOption={false}
            acceptedFormats={IMAGE_FORMATS.concat(
              VIDEO_FORMATS,
              ADS_CAMPAIGN_CAROUSEL_FORMAT
            )}
            isOutsideCampaign={true}
            pathStorage={`adsCampaigns/${userID}/`}
            onCloseModal={() => setGalleryOpen(false)}
            onCallbackSelectedItems={onGallerySelected}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default BulkCampaignsContents;
